import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, TrendingUp } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './StockInvestmentCalculatorPage/components/CalculationForm';
import ResultsOverview from './StockInvestmentCalculatorPage/components/ResultsOverview';
import PortfolioChart from './StockInvestmentCalculatorPage/components/PortfolioChart';
import QuickAdjustmentSidebar from './StockInvestmentCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './StockInvestmentCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './StockInvestmentCalculatorPage/components/TipsSidebar';
import InfoContent from './StockInvestmentCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Базовые параметры
  initialInvestment: '100000',
  monthlyContribution: '10000',
  investmentTerm: '10', // Лет
  startDate: new Date().toISOString().split('T')[0],
  
  // Структура портфеля
  portfolioType: 'balanced', // 'conservative', 'balanced', 'aggressive', 'custom'
  assetAllocation: {
    stocks: 50, // % акций
    bonds: 30, // % облигаций
    cash: 10, // % денежного рынка (вклады, ОФЗ)
    gold: 5, // % золота
    realEstate: 5, // % недвижимости (ПИФы, ETF)
    custom: [] // Пользовательская структура
  },
  
  // Настройки доходности
  expectedReturns: {
    stocks: '15', // % годовых
    bonds: '9', // % годовых
    cash: '7', // % годовых
    gold: '8', // % годовых
    realEstate: '10' // % годовых
  },
  
  // Настройки инфляции
  includeInflation: true,
  inflationRate: '7', // % годовых
  
  // Настройки налогов
  includeTaxes: true,
  taxRate: '13', // Налог на доходы (ставка НДФЛ в России)
  
  // Настройки комиссий
  includeFees: true,
  brokerFee: '0.3', // % от суммы сделки
  managementFee: '1', // % годовых от активов
  
  // Настройки для ИИС
  useIIS: false,
  iisType: 'taxDeduction', // 'taxDeduction' или 'taxFree'
  iisDeductionLimit: '400000', // Лимит вычета на ИИС тип А (возврат до 52т.р. в год)
  
  // Пополнения/снятия
  includeCashFlow: false,
  additionalInvestments: [],
  withdrawals: []
};

// Определение типов инвестиционных портфелей
export const portfolioTypes = [
  { 
    id: 'conservative', 
    name: 'Консервативный', 
    description: 'Минимальный риск, упор на облигации и защитные активы',
    allocation: {
      stocks: 20,
      bonds: 60,
      cash: 15,
      gold: 5,
      realEstate: 0
    },
    expectedReturn: 9.2,
    riskLevel: 'Низкий'
  },
  { 
    id: 'balanced', 
    name: 'Сбалансированный', 
    description: 'Оптимальное сочетание риска и доходности',
    allocation: {
      stocks: 50,
      bonds: 30,
      cash: 10,
      gold: 5,
      realEstate: 5
    },
    expectedReturn: 11.5,
    riskLevel: 'Средний'
  },
  { 
    id: 'aggressive', 
    name: 'Агрессивный', 
    description: 'Максимальная доходность при высоком риске',
    allocation: {
      stocks: 80,
      bonds: 10,
      cash: 0,
      gold: 0,
      realEstate: 10
    },
    expectedReturn: 14.2,
    riskLevel: 'Высокий'
  },
  { 
    id: 'custom', 
    name: 'Пользовательский', 
    description: 'Настраиваемая структура портфеля',
    allocation: {
      stocks: 50,
      bonds: 30,
      cash: 10,
      gold: 5,
      realEstate: 5
    },
    expectedReturn: 0,
    riskLevel: 'Зависит от структуры'
  }
];

// Определение типов активов
export const assetTypes = [
  { id: 'stocks', name: 'Акции', color: '#4F46E5', defaultReturn: 15 },
  { id: 'bonds', name: 'Облигации', color: '#10B981', defaultReturn: 9 },
  { id: 'cash', name: 'Денежный рынок', color: '#6B7280', defaultReturn: 7 },
  { id: 'gold', name: 'Золото', color: '#F59E0B', defaultReturn: 8 },
  { id: 'realEstate', name: 'Недвижимость', color: '#EC4899', defaultReturn: 10 }
];

/**
 * StockInvestmentCalculatorPage Component - Полный калькулятор инвестиций в ценные бумаги с учетом
 * российских реалий, налогообложения, ИИС и различных стратегий.
 */
const StockInvestmentCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('stock-investment-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор инвестиций в ценные бумаги | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор инвестиций в ценные бумаги: расчет потенциальной доходности, учет налогов, комиссий, ИИС, различных стратегий и структуры портфеля для российского рынка.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  const [assetAllocationChart, setAssetAllocationChart] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedPortfolios, setSavedPortfolios] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных портфелей из localStorage ---
  useEffect(() => {
    const savedPortfoliosData = localStorage.getItem('savedPortfolios');
    if (savedPortfoliosData) {
      try {
        setSavedPortfolios(JSON.parse(savedPortfoliosData));
      } catch (e) {
        console.error("Error loading saved portfolios:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const getPortfolioType = (id) => {
        const found = portfolioTypes.find(t => t.id === id);
        return found || portfolioTypes.find(t => t.id === 'balanced');
      };

      switch (subToolId) {
        case 'conservative':
          newState.portfolioType = 'conservative';
          newState.assetAllocation = getPortfolioType('conservative').allocation;
          break;
        case 'balanced':
          newState.portfolioType = 'balanced';
          newState.assetAllocation = getPortfolioType('balanced').allocation;
          break;
        case 'aggressive':
          newState.portfolioType = 'aggressive';
          newState.assetAllocation = getPortfolioType('aggressive').allocation;
          break;
        case 'iis-calculator':
          newState.useIIS = true;
          newState.portfolioType = 'balanced';
          newState.assetAllocation = getPortfolioType('balanced').allocation;
          break;
        case 'retirement':
          newState.investmentTerm = '30';
          newState.portfolioType = 'balanced';
          newState.assetAllocation = getPortfolioType('balanced').allocation;
          break;
        case 'dividend':
          newState.portfolioType = 'custom';
          // Настройка портфеля с дивидендными акциями
          newState.assetAllocation = {
            stocks: 70,
            bonds: 20,
            cash: 5,
            gold: 0,
            realEstate: 5
          };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setPortfolioHistory([]);
    setAssetAllocationChart([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение портфеля в localStorage
  const savePortfolio = (name) => {
    if (!results) return;

    const newSavedPortfolio = {
      id: Date.now().toString(),
      name: name || `Портфель ${formData.initialInvestment}₽ на ${formData.investmentTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      portfolioType: formData.portfolioType
    };

    const updatedSavedPortfolios = [...savedPortfolios, newSavedPortfolio];
    setSavedPortfolios(updatedSavedPortfolios);
    localStorage.setItem('savedPortfolios', JSON.stringify(updatedSavedPortfolios));

    return newSavedPortfolio;
  };

  // Удаление сохраненного портфеля
  const deleteSavedPortfolio = (id) => {
    const updatedSavedPortfolios = savedPortfolios.filter(portfolio => portfolio.id !== id);
    setSavedPortfolios(updatedSavedPortfolios);
    localStorage.setItem('savedPortfolios', JSON.stringify(updatedSavedPortfolios));
  };

  // Загрузка сохраненного портфеля
  const loadSavedPortfolio = (id) => {
    const portfolioToLoad = savedPortfolios.find(portfolio => portfolio.id === id);
    if (portfolioToLoad) {
      setFormData(portfolioToLoad.formData);
      setResults(portfolioToLoad.results);
      // Восстановим также историю портфеля и графики
      if (portfolioToLoad.portfolioHistory) {
        setPortfolioHistory(portfolioToLoad.portfolioHistory);
      }
      if (portfolioToLoad.assetAllocationChart) {
        setAssetAllocationChart(portfolioToLoad.assetAllocationChart);
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор инвестиций в ценные бумаги'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              portfolioHistory={portfolioHistory}
              setPortfolioHistory={setPortfolioHistory}
              setAssetAllocationChart={setAssetAllocationChart}
              portfolioTypes={portfolioTypes}
              assetTypes={assetTypes}
              savePortfolio={savePortfolio}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PortfolioChart 
                  portfolioHistory={portfolioHistory}
                  assetAllocationChart={assetAllocationChart}
                  results={results}
                  formData={formData}
                  assetTypes={assetTypes}
                />
              </>
            )}

            {/* --- Информационный контент о инвестициях в ценные бумаги --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              portfolioTypes={portfolioTypes}
            />

            {/* --- Панель сравнения портфелей --- */}
            <ComparisonSidebar 
              savedPortfolios={savedPortfolios}
              deleteSavedPortfolio={deleteSavedPortfolio}
              loadSavedPortfolio={loadSavedPortfolio}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Range Slider Styling */
        input[type="range"] {
          @apply h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600;
        }
        input[type="range"]::-webkit-slider-thumb {
          @apply appearance-none w-4 h-4 rounded-full bg-indigo-600 dark:bg-indigo-500 cursor-pointer;
        }
        input[type="range"]::-moz-range-thumb {
          @apply w-4 h-4 rounded-full bg-indigo-600 dark:bg-indigo-500 cursor-pointer;
        }
      `}</style>
    </div>
  );
};

export default StockInvestmentCalculatorPage;